import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Icon, Input } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { Section } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { Card, CardRepository } from '~/repositories/CardRepository';
import { SimulateRepository } from '~/repositories/SimulateRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { FormElement, FormLabel, Inner } from '~/styles';

import { CardProps } from './Card';

interface Params {
  id: string;
}

const cardQueryKey = (id: string) => ['card', id] as const;

const ActionCard = styled.div<{ $type?: 'success' | 'warning' | 'default' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme, $type }) =>
    $type === 'success'
      ? theme.success.blendToBackground(50)
      : $type === 'warning'
        ? theme.warning.blendToBackground(50)
        : theme.secondary.blendToBackground(50)};
  border: 1px solid
    ${({ theme, $type }) =>
      $type === 'success'
        ? theme.success.blendToBackground(150)
        : $type === 'warning'
          ? theme.warning.blendToBackground(150)
          : theme.secondary.blendToBackground(100)};
`;

const SimulationActionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.secondary.blendToBackground(50)};
  border: 1px solid ${({ theme }) => theme.secondary.blendToBackground(100)};
`;

const SimulationForm = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const SimulationFormActions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
`;

const ActionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionIconWrapper = styled.div<{ $type?: 'success' | 'warning' | 'default' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${({ theme, $type }) =>
    $type === 'success'
      ? theme.success.blendToBackground(100)
      : $type === 'warning'
        ? theme.warning.blendToBackground(100)
        : theme.secondary.blendToBackground(100)};

  svg {
    --icon-size: 20px;
    --icon-color: ${({ theme, $type }) =>
      $type === 'success'
        ? theme.success.toString()
        : $type === 'warning'
          ? theme.warning.toString()
          : theme.secondary.blendToBackground(700)};
  }
`;

const ActionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ActionTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.foreground};
`;

const ActionDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.secondary.blendToBackground(700)};
`;

const ActionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NoActionsText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  padding: 8px 0;
`;

export const PageCardActions: React.FC = () => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isSandbox } = useSessionStore();
  const { id } = useParams<keyof Params>() as Params;
  const { card: outletCard } = useOutletContext<CardProps>();

  const [authAmount, setAuthAmount] = useState('1000');
  const [authCurrency, setAuthCurrency] = useState('USD');
  const [description, setDescription] = useState('');

  const { data: card, error: cardError } = useQuery({
    queryKey: cardQueryKey(id!),
    queryFn: () => CardRepository.get(id!),
    enabled: !!id && !outletCard,
    initialData: outletCard,
  });

  const pauseCardMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('Card ID is required');
      return CardRepository.pause(id);
    },
    onSuccess: (response: Card) => {
      queryClient.setQueryData(cardQueryKey(id!), response);
      addSuccessNotification({
        content: 'Card paused successfully',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error?.message || 'Failed to pause card',
      });
    },
  });

  const activateCardMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('Card ID is required');
      return CardRepository.activate(id);
    },
    onSuccess: (response: Card) => {
      queryClient.setQueryData(cardQueryKey(id!), response);
      addSuccessNotification({
        content: 'Card activated successfully',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error?.message || 'Failed to activate card',
      });
    },
  });

  useEffect(() => {
    setDescription(card?.description ?? '');
  }, [card?.description]);

  const updateDescriptionMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('Card ID is required');
      return CardRepository.update(id, { description: description.trim() });
    },
    onSuccess: (response: Card) => {
      queryClient.setQueryData(cardQueryKey(id!), response);
      addSuccessNotification({ content: 'Card description updated' });
    },
    onError: (error: any) => {
      addDangerNotification({ content: error?.message || 'Failed to update card description' });
    },
  });

  const simulateAuthMutation = useMutation({
    mutationFn: () => {
      if (!id) throw new Error('Card ID is required');
      const amount = parseInt(authAmount, 10);
      if (!Number.isFinite(amount) || amount <= 0) throw new Error('Amount must be a positive number');
      if (!authCurrency.trim()) throw new Error('Currency is required');
      return SimulateRepository.simulateCardAuthorization(id, {
        cardholderAmount: amount,
        merchantCurrency: authCurrency.trim(),
      });
    },
    onSuccess: () => {
      addSuccessNotification({ content: 'Authorization simulated successfully' });
    },
    onError: (error: any) => {
      addDangerNotification({ content: error?.message || 'Failed to simulate authorization' });
    },
  });

  useEffect(() => {
    if (cardError) {
      navigate(ROUTE.CARDS);
      addDangerNotification({
        content: (cardError as any).message || 'Failed to load card',
      });
    }
  }, [cardError, navigate, addDangerNotification]);

  if (!card) {
    return null;
  }

  const canPause = card.status === 'active';
  const canActivate = card.status === 'paused';
  const isActionPending =
    pauseCardMutation.isPending ||
    activateCardMutation.isPending ||
    simulateAuthMutation.isPending ||
    updateDescriptionMutation.isPending;
  const isDescriptionUnchanged = description.trim() === (card.description ?? '');
  const showSandboxSimulation = !!isSandbox;

  return (
    <Inner p={0}>
      <Section headline="Card Status">
        <Inner px="var(--page-margin)" py={16}>
          <ActionsGrid>
            {canActivate && (
              <ActionCard $type="warning">
                <ActionInfo>
                  <ActionIconWrapper $type="warning">
                    <Icon.Offline />
                  </ActionIconWrapper>
                  <ActionText>
                    <ActionTitle>Card is Paused</ActionTitle>
                    <ActionDescription>
                      This card is temporarily disabled and cannot be used for transactions.
                    </ActionDescription>
                  </ActionText>
                </ActionInfo>
                <Button
                  variant="primary"
                  icon={<Icon.Play />}
                  onClick={() => activateCardMutation.mutate()}
                  isLoading={activateCardMutation.isPending}
                  isDisabled={isActionPending}
                >
                  Activate Card
                </Button>
              </ActionCard>
            )}

            {canPause && (
              <ActionCard $type="success">
                <ActionInfo>
                  <ActionIconWrapper $type="success">
                    <Icon.CircleCheck />
                  </ActionIconWrapper>
                  <ActionText>
                    <ActionTitle>Card is Active</ActionTitle>
                    <ActionDescription>This card can be used for transactions.</ActionDescription>
                  </ActionText>
                </ActionInfo>
                <Button
                  variant="secondary"
                  icon={<Icon.Offline />}
                  onClick={() => pauseCardMutation.mutate()}
                  isLoading={pauseCardMutation.isPending}
                  isDisabled={isActionPending}
                >
                  Pause Card
                </Button>
              </ActionCard>
            )}

            {!canPause && !canActivate && <NoActionsText>No actions available for this card status.</NoActionsText>}
          </ActionsGrid>
        </Inner>
      </Section>

      <Section headline="Description">
        <Inner px="var(--page-margin)" py={16}>
          <SimulationForm>
            <FormElement>
              <FormLabel>Description</FormLabel>
              <Input
                value={description}
                onChange={(value: string) => setDescription(value)}
                placeholder="Add a description"
                maxLength={255}
              />
            </FormElement>
            <SimulationFormActions>
              <Button
                variant="primary"
                onClick={() => updateDescriptionMutation.mutate()}
                isLoading={updateDescriptionMutation.isPending}
                isDisabled={isActionPending || isDescriptionUnchanged}
              >
                Save Description
              </Button>
            </SimulationFormActions>
          </SimulationForm>
        </Inner>
      </Section>

      {showSandboxSimulation && (
        <Section headline="Sandbox Simulation">
          <Inner px="var(--page-margin)" py={16}>
            <ActionsGrid>
              <SimulationActionCard>
                <ActionInfo>
                  <ActionIconWrapper>
                    <Icon.Sandbox />
                  </ActionIconWrapper>
                  <ActionText>
                    <ActionTitle>Simulate Authorization</ActionTitle>
                    <ActionDescription>Simulate a card authorization transaction.</ActionDescription>
                  </ActionText>
                </ActionInfo>
                <SimulationForm>
                  <FormElement>
                    <FormLabel>Amount (cents)</FormLabel>
                    <Input value={authAmount} onChange={(value: string) => setAuthAmount(value)} placeholder="1000" />
                  </FormElement>
                  <FormElement>
                    <FormLabel>Currency</FormLabel>
                    <Input
                      value={authCurrency}
                      onChange={(value: string) => setAuthCurrency(value)}
                      placeholder="USD"
                    />
                  </FormElement>
                  <SimulationFormActions>
                    <Button
                      variant="primary"
                      icon={<Icon.Sandbox />}
                      onClick={() => simulateAuthMutation.mutate()}
                      isLoading={simulateAuthMutation.isPending}
                      isDisabled={isActionPending || !authAmount.trim() || !authCurrency.trim()}
                    >
                      Simulate Authorization
                    </Button>
                  </SimulationFormActions>
                </SimulationForm>
              </SimulationActionCard>
            </ActionsGrid>
          </Inner>
        </Section>
      )}
    </Inner>
  );
};
